import { driver } from 'driver.js';
import type { DriveStep, Driver } from 'driver.js';
import { getPageTutorial, PageTutorialDefinition, PageTutorialStep } from '@/support/pageTutorialDefinitions';

interface StartTutorialOptions {
  seenZoneKeys?: string[];
  onFinished?: (definition: PageTutorialDefinition, presentedZoneKeys: string[]) => void;
}

let activeDriver: Driver | null = null;
let activeRunId = 0;
const suppressedDestroyedRunIds = new Set<number>();

interface ResolvedTutorialSteps {
  steps: DriveStep[];
  zoneKeys: string[];
}

const selectorExists = (selector?: string): boolean => {
  if (!selector) return true;
  if (typeof document === 'undefined') return false;

  try {
    return Boolean(document.querySelector(selector));
  } catch {
    return false;
  }
};

const toDriverStep = (step: PageTutorialStep): DriveStep => ({
  element: step.selector,
  popover: {
    title: step.title,
    description: step.description,
    side: step.side || 'bottom',
    align: step.align || 'start',
  },
});

const fallbackZoneKeyFor = (step: PageTutorialStep): string | null => {
  if (step.zoneKey) return step.zoneKey;
  if (!step.selector) return null;

  return `selector_${step.selector.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '')}`;
};

const resolveSteps = (definition: PageTutorialDefinition, seenZoneKeys: string[] = []): ResolvedTutorialSteps => {
  const seenZones = new Set(seenZoneKeys);
  const visibleSteps = definition.steps.filter((step) => {
    const zoneKey = fallbackZoneKeyFor(step);
    return (!zoneKey || !seenZones.has(zoneKey)) && selectorExists(step.selector);
  });

  return {
    steps: visibleSteps.map(toDriverStep),
    zoneKeys: visibleSteps
      .map(fallbackZoneKeyFor)
      .filter((zoneKey): zoneKey is string => Boolean(zoneKey)),
  };
};

export const hasPageTutorial = (routeName: unknown): boolean => {
  return Boolean(getPageTutorial(routeName));
};

export const getPageTutorialDefinition = (routeName: unknown): PageTutorialDefinition | null => {
  return getPageTutorial(routeName);
};

export const destroyActiveTutorial = (notify = false): void => {
  if (!activeDriver) return;

  const driverToDestroy = activeDriver;
  const runId = activeRunId;
  if (!notify) {
    suppressedDestroyedRunIds.add(runId);
  }

  driverToDestroy.destroy();
};

export const isPageTutorialActive = (): boolean => {
  return Boolean(activeDriver);
};

export const startPageTutorial = (routeName: unknown, options: StartTutorialOptions = {}): boolean => {
  const definition = getPageTutorial(routeName);
  if (!definition || typeof window === 'undefined') return false;

  const { steps, zoneKeys } = resolveSteps(definition, options.seenZoneKeys || []);
  if (!steps.length) return false;

  destroyActiveTutorial(false);
  const runId = activeRunId + 1;
  activeRunId = runId;

  const driverInstance = driver({
    steps,
    animate: true,
    allowClose: true,
    allowKeyboardControl: true,
    disableActiveInteraction: false,
    overlayClickBehavior: 'close',
    overlayOpacity: 0.62,
    popoverClass: 'suite241-driver-popover',
    popoverOffset: 12,
    showButtons: ['previous', 'next', 'close'],
    showProgress: true,
    smoothScroll: true,
    stagePadding: 8,
    stageRadius: 8,
    nextBtnText: 'Suivant',
    prevBtnText: 'Précédent',
    doneBtnText: 'Terminer',
    progressText: '{{current}}/{{total}}',
    onDestroyed: () => {
      const shouldNotify = !suppressedDestroyedRunIds.has(runId);
      suppressedDestroyedRunIds.delete(runId);
      if (activeDriver === driverInstance) {
        activeDriver = null;
      }

      if (shouldNotify) {
        options.onFinished?.(definition, zoneKeys);
      }
    },
  });

  activeDriver = driverInstance;
  activeDriver.drive();

  return true;
};
